import React, { FC, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as BellIcon } from "assets/icons/bell.svg";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { useActiveOrg } from "context/ActiveOrgContext";
import { searchSystem } from "services/search.service";
import { CONTACT_TYPE } from "services/contacts.service";
import vgToast from "utils/toast";

type HeaderProps = {
  /** Opens the navigation drawer. Only rendered below lg. */
  onMenuClick: () => void;
};

const Header = ({ onMenuClick }: HeaderProps) => {
  const { pathname } = useLocation();
  const segment = pathname.split("/")[2];
  const title =
    !segment || segment === "dashboard"
      ? "Dashboard"
      : segment.replace(/-/g, " ");

  return (
    <header className="bg-white h-16 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 md:px-6 border-b border-slate-200 z-10">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation"
          className="-ml-1 shrink-0 rounded-md p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/30 lg:hidden"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path
              d="M3 5h14M3 10h14M3 15h14"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <h2 className="truncate capitalize text-sm font-medium text-slate-500">
          {title}
        </h2>
      </div>

      {/* Search is the first thing to go on a narrow header - the org switcher
          and account menu matter more, and search has its own room from md up. */}
      <div className="flex shrink-0 items-center gap-x-2 sm:gap-x-4">
        <OrgSwitcher />
        <div className="hidden md:block">
          <SearchBox />
        </div>
        <Notification />
        <UserInfo />
      </div>
    </header>
  );
};

export default Header;

const OrgSwitcher: FC = () => {
  const { orgId, organisations, setActiveOrg, loading } = useActiveOrg();

  if (loading && organisations.length === 0) {
    return (
      <span className="text-xs text-slate-400 whitespace-nowrap">Providers…</span>
    );
  }

  const selectValue =
    orgId > 0 && organisations.some((o) => o.orgId === orgId)
      ? String(orgId)
      : organisations[0]
      ? String(organisations[0].orgId)
      : "";

  return (
    <div className="flex min-w-0 flex-col sm:min-w-[10rem] sm:max-w-[16rem]">
      <label className="text-[10px] uppercase text-slate-400 leading-none mb-0.5">
        Active provider
      </label>
      <select
        className="text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white truncate focus:outline-none focus:ring-2 focus:ring-vg-blue/20 focus:border-vg-blue/40"
        value={selectValue}
        disabled={organisations.length === 0}
        onChange={(e) => {
          const id = Number(e.target.value);
          const o = organisations.find((x) => x.orgId === id);
          if (o) setActiveOrg(o.orgId, o.orgName);
        }}
      >
        {organisations.length === 0 ? (
          <option value="">No providers</option>
        ) : (
          organisations.map((o) => (
            <option key={o.orgId} value={o.orgId}>
              {o.orgName || `Provider ${o.orgId}`}
            </option>
          ))
        )}
      </select>
    </div>
  );
};

const SearchBox: FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [animals, setAnimals] = useState<Array<{ id: number; title: string }>>([]);
  const [contacts, setContacts] = useState<Array<{ id: number; title: string; subtitle?: string }>>([]);
  const [staff, setStaff] = useState<Array<{ id: number; title: string; subtitle?: string }>>([]);
  const [services, setServices] = useState<Array<{ id: number; title: string; subtitle?: string }>>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (!isAuthenticated || orgId <= 0 || trimmed.length < 2) {
      setAnimals([]);
      setContacts([]);
      setStaff([]);
      setServices([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await searchSystem(token, orgId, trimmed);
        if (cancelled) return;
        if (error) {
          setAnimals([]);
          setContacts([]);
          setStaff([]);
          setServices([]);
          vgToast.error(error.message);
          return;
        }
        setAnimals(
          (data?.animalList ?? [])
            .filter((item) => Number.isFinite(item.animalId) && item.animalId > 0)
            .slice(0, 5)
            .map((item) => ({
              id: item.animalId,
              title: item.name?.trim() || `Patient ${item.animalId}`,
            }))
        );
        // Backend returns every contact type in contactList — split staff
        // (UserMember) out from customers so each routes to its own page.
        const allContacts = (data?.contactList ?? []).filter(
          (item) => Number.isFinite(item.contactId) && item.contactId > 0
        );
        const titleOf = (item: typeof allContacts[number], fallback: string) =>
          item.contactFullName?.trim() ||
          [item.contactFirstName, item.contactLastName].filter(Boolean).join(" ").trim() ||
          fallback;

        setContacts(
          allContacts
            .filter(
              (item) =>
                item.contactType !== CONTACT_TYPE.USER_MEMBER &&
                item.contactType !== CONTACT_TYPE.SUPPLIER
            )
            .slice(0, 5)
            .map((item) => ({
              id: item.contactId,
              title: titleOf(item, `Client ${item.contactId}`),
              subtitle: item.emailAddress?.trim() || undefined,
            }))
        );

        setStaff(
          allContacts
            .filter(
              (item) =>
                item.contactType === CONTACT_TYPE.USER_MEMBER &&
                Number.isFinite(item.userId) &&
                (item.userId ?? 0) > 0
            )
            .slice(0, 5)
            .map((item) => ({
              id: item.userId as number,
              title: titleOf(item, `Staff ${item.userId}`),
              subtitle: item.emailAddress?.trim() || undefined,
            }))
        );
        setServices(
          (data?.serviceList ?? [])
            .filter((item) => Number.isFinite(item.serviceId) && item.serviceId > 0)
            .slice(0, 5)
            .map((item) => ({
              id: item.serviceId,
              title: item.name?.trim() || `Service ${item.serviceId}`,
              subtitle: item.description?.trim() || undefined,
            }))
        );
      } catch (e) {
        if (!cancelled) {
          setAnimals([]);
          setContacts([]);
          setStaff([]);
          setServices([]);
          vgToast.error((e as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [getAccessTokenSilently, isAuthenticated, orgId, query]);

  const hasResults =
    animals.length > 0 ||
    contacts.length > 0 ||
    staff.length > 0 ||
    services.length > 0;

  const handlePick = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="bg-slate-50 flex items-center gap-2 px-3.5 py-2 rounded-md border border-slate-200 focus-within:border-vg-blue/40 focus-within:ring-2 focus-within:ring-vg-blue/15 transition-colors">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="bg-transparent min-w-[12rem] md:min-w-[20rem] focus:outline-none text-sm placeholder:text-slate-400"
          placeholder="Search here …"
        />
        <SearchIcon className="" />
      </div>
      {open && query.trim().length >= 2 && (
        <div className="absolute right-0 top-full mt-2 w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md z-50">
          {loading ? (
            <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
          ) : hasResults ? (
            <div className="py-2">
              {animals.length > 0 && (
                <SearchSection
                  title="Deceased"
                  items={animals.map((item) => ({
                    key: `animal-${item.id}`,
                    title: item.title,
                    onClick: () => handlePick(`/app/patients/${item.id}`),
                  }))}
                />
              )}
              {contacts.length > 0 && (
                <SearchSection
                  title="Clients"
                  items={contacts.map((item) => ({
                    key: `contact-${item.id}`,
                    title: item.title,
                    subtitle: item.subtitle,
                    onClick: () => handlePick(`/app/clients/${item.id}`),
                  }))}
                />
              )}
              {staff.length > 0 && (
                <SearchSection
                  title="Staff"
                  items={staff.map((item) => ({
                    key: `staff-${item.id}`,
                    title: item.title,
                    subtitle: item.subtitle,
                    onClick: () => handlePick(`/app/staff/${item.id}`),
                  }))}
                />
              )}
              {services.length > 0 && (
                <SearchSection
                  title="Services"
                  items={services.map((item) => ({
                    key: `service-${item.id}`,
                    title: item.title,
                    subtitle: item.subtitle,
                    onClick: () => handlePick(`/app/services/${item.id}`),
                  }))}
                />
              )}
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-slate-500">No results found.</div>
          )}
        </div>
      )}
    </div>
  );
};

const SearchSection: FC<{
  title: string;
  items: Array<{
    key: string;
    title: string;
    subtitle?: string;
    onClick: () => void;
  }>;
}> = ({ title, items }) => {
  return (
    <div className="py-1">
      <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {title}
      </div>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={item.onClick}
          className="block w-full px-4 py-2 text-left hover:bg-slate-50"
        >
          <div className="text-sm text-slate-900 truncate">{item.title}</div>
          {item.subtitle && (
            <div className="text-xs text-slate-500 truncate">{item.subtitle}</div>
          )}
        </button>
      ))}
    </div>
  );
};

const Notification: FC = () => {
  return (
    <div>
      <BellIcon />
    </div>
  );
};

const UserInfo: FC = () => {
  const { user, logout } = useAuth0();
  const { orgName } = useActiveOrg();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setOpen(false);
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="relative flex items-center" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center text-left rounded-md hover:bg-slate-50 py-1 px-2 -mr-2 transition-colors"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <img
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          src={user.picture}
          alt=""
        />
        <div className="ml-2 min-w-0 max-w-[12rem]">
          <h2 className="text-sm font-medium truncate">{user.name}</h2>
          <p className="text-xs text-slate-500 truncate">
            {orgName || user.email}
          </p>
        </div>
        <span
          className="ml-1 text-slate-400 text-xs flex-shrink-0"
          aria-hidden
        >
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 min-w-[12rem] max-w-[calc(100vw-1.5rem)] rounded-md border border-slate-200 bg-white py-1 shadow-md"
          role="menu"
        >
          <Link
            to="/app/profile"
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            Profile
          </Link>
          <button
            type="button"
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            role="menuitem"
            onClick={handleLogout}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
};
